import { useQueryCompanyDelegations } from "@/api/company/company";
import { CompanyDelegationDtoClient } from "@/api/company/dto/company-delegation.dto";
import { PayementStatus } from "@/api/enums/PayementStatus";
import { Badge } from "@/components/atoms/Badge";
import { Input } from "@/components/atoms/Input";
import { Skeleton } from "@/components/atoms/Skeleton";
import FileLink from "@/components/molecules/FileLink";
import LoadingError from "@/components/molecules/LoadingError";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/molecules/Table";
import { Perimeter } from "@/lib/perimeter";
import { cn, formatDate } from "@/lib/utils";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";

const columnHelper = createColumnHelper<CompanyDelegationDtoClient>();

const Candidates = () => {
  const [searchText, setSearchText] = useState("");

  /////////////////////////
  // api calls
  /////////////////////////

  const delegationsQuery = useQueryCompanyDelegations();
  const { data: delegations, isLoading } = delegationsQuery;
  const filteredData = useMemo(() => {
    if (!delegations) return [];
    return delegations.filter(
      ({ payementStatus, candidateEmail, candidateName, candidateSurname }) =>
        ((payementStatus === PayementStatus.DELEGATION_PAID_CARD ||
          payementStatus === PayementStatus.DELEGATION_PAID_TRANSFER) &&
          candidateEmail.toLowerCase().includes(searchText.toLowerCase())) ||
        candidateName.toLowerCase().includes(searchText.toLowerCase()) ||
        candidateSurname.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [delegations, searchText]);

  /////////////////////////
  // table
  /////////////////////////

  const columns = useMemo(
    () => [
      columnHelper.accessor("exam.date", {
        id: "examDate",
        header: "Date examen",
        cell: (info) => (
          <span className="text-gray-600 font-medium">
            {formatDate(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor(
        ({ candidateEmail, candidateName, candidateSurname }) => ({
          candidateEmail,
          candidateName,
          candidateSurname,
        }),
        {
          id: "user",
          header: "Candidat",
          cell: (info) => {
            const { candidateEmail, candidateName, candidateSurname } =
              info.getValue();
            return (
              <span className="text-gray-900 font-medium w-full">
                {candidateSurname + " " + candidateName}
                <br />
                <span className="text-gray-500 font-normal">
                  {candidateEmail}
                </span>
              </span>
            );
          },
        },
      ),
      columnHelper.accessor("result", {
        id: "validityDate",
        header: "Fin de validité",
        cell: (info) => {
          const validityEndDate = info.getValue()?.validityEndDate;
          if (!validityEndDate) return <></>;

          return (
            <span className="text-gray-600 font-medium">
              {formatDate(validityEndDate)}
            </span>
          );
        },
      }),
      columnHelper.accessor("result", {
        id: "perimeter",
        header: "Pér.",
        cell: (info) => {
          const perimeter = info.getValue()?.perimeter;
          if (!perimeter) return <></>;

          return (
            <Badge className="text-gray-700" variant="white">
              {Perimeter.toCode(perimeter)}
            </Badge>
          );
        },
      }),
      columnHelper.accessor("debitNote", {
        id: "debitNote",
        header: "Note de débit",
        cell: (info) => <FileLink file={info.getValue()} showName={false} />,
      }),
      columnHelper.accessor("convocation", {
        id: "convocation",
        header: "Convocation",
        cell: (info) => <FileLink file={info.getValue()} showName={false} />,
      }),
      columnHelper.accessor("result", {
        id: "attendanceCertificate",
        header: "Attestation de présence",
        cell: (info) => (
          <FileLink
            file={info.getValue()?.attendanceCertificate}
            showName={false}
          />
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    // data: delegationsQuery.data ?? [],
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  /////////////////////////
  // component
  /////////////////////////

  if (!delegations)
    return (
      <div className="py-3 px-6 bg-white w-full h-full">
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : (
          <LoadingError>Erreur de chargement des candidats</LoadingError>
        )}
      </div>
    );

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-col w-full border-b border-b-gray-200">
        <div className="flex flex-col gap-2 py-3 px-6 bg-white w-full">
          <h1>Vos candidats</h1>
        </div>
      </div>
      <div className="py-4 px-6 flex flex-col gap-3">
        <Input
          className="shadow-none"
          id="shadcn-input"
          searchIcon
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Rechercher"
        />
        <div className="rounded-lg overflow-auto border border-gray-200">
          <Table className="text-gray-500 text-sm font-normal leading-6">
            <TableHeader className="items-center bg-gray-50">
              <TableRow>
                {table.getHeaderGroups()[0].headers.map((header) => (
                  <TableHead key={header.id}>
                    <div className="flex flex-row items-center gap-3 text-xs">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row, i) => (
                <TableRow key={row.id} className={cn({ "bg-gray-50": i % 2 })}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="h-16 py-0">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default Candidates;
